import { useEffect, useMemo, useState, useCallback } from "react";
import { getRoles, createRole, updateRole } from "../../../services/usuarios_tienda/roles";
import RolFormModal from "./RolesFormModal";
import AsignarPermisosModal from "../roles/AsignarPermisosModal";
import { IconEdit, IconConfig } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./RolesPage.css";

const PAGE_SIZE = 12;

function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPermisosModal, setShowPermisosModal] = useState(false);
  const [roleForPermisos, setRoleForPermisos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState(null);

  const getNiceErrorMessage = (error, fallback = "Ocurrió un error.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 220 ? clean.slice(0, 220) + "…" : clean;
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => setAlert(null), 3500);
  };

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const array = await getRoles();
      setRoles(Array.isArray(array) ? array : []);
    } catch (err) {
      console.error("Error cargando roles:", err);
      showAlert("error", getNiceErrorMessage(err, "Error cargando roles."));
    } finally {
      setLoading(false);
    }
    // showAlert se excluye a propósito (se recrea cada render); incluirlo
    // recrearía loadRoles y dispararía recargas en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((r) => String(r?.nombre || "").toLowerCase().includes(term));
  }, [roles, search]);

  const totalPages = useMemo(() => {
    const tp = Math.ceil(filtered.length / PAGE_SIZE);
    return tp > 0 ? tp : 1;
  }, [filtered.length]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const currentData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const nextPage = () => page < totalPages && setPage(page + 1);
  const prevPage = () => page > 1 && setPage(page - 1);

  const goToPage = (n) => {
    if (n < 1 || n > totalPages || n === page) return;
    setPage(n);
  };

  const visiblePages = useMemo(() => {
    const count = 3;
    if (totalPages <= count) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let s = Math.max(1, page - Math.floor(count / 2));
    let e = s + count - 1;
    if (e > totalPages) {
      e = totalPages;
      s = e - count + 1;
    }
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }, [page, totalPages]);

  const openCreateModal = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  const openEditModal = (rol) => {
    setSelectedRole(rol);
    setShowModal(true);
  };

  const openPermisosModal = (rol) => {
    setRoleForPermisos(rol);
    setShowPermisosModal(true);
  };

  const handleCreate = async (formData) => {
    setUpdating(true);
    try {
      await createRole(formData);
      await loadRoles();
      setShowModal(false);
      showAlert("success", "Rol creado correctamente.");
    } catch (err) {
      console.error("Error creating role:", err);
      showAlert("error", getNiceErrorMessage(err, "Error creando rol."));
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdate = async (id, formData) => {
    setUpdating(true);
    try {
      await updateRole(id, formData);
      await loadRoles();
      setShowModal(false);
      showAlert("success", "Rol actualizado correctamente.");
    } catch (err) {
      console.error("Error updating role:", err);
      showAlert("error", getNiceErrorMessage(err, "Error actualizando rol."));
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="tienda-page-scope">

      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>✕</button>
        </div>
      )}

      <div className="header">
        <div className="methods-header__row">
          <h2 className="title">Gestión de roles</h2>

          {hasPerm("create_rol") && (
            <button
              type="button"
              className="methods-btn-create"
              onClick={openCreateModal}
              disabled={loading || updating}
              title={loading ? "Cargando..." : updating ? "Actualizando..." : ""}
              style={{ opacity: loading || updating ? 0.7 : 1 }}
            >
              {updating ? "Por favor espera..." : "Crear rol"}
            </button>
          )}
        </div>

        <p className="page-lead">
          Administra los roles de tu tienda y los permisos que tiene cada uno.
        </p>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Buscar rol..."
            className="search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre del rol</th>
              <th>Estado</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="no-data">Cargando roles...</td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">No se encontraron roles</td>
              </tr>
            ) : (
              currentData.map((r) => (
                <tr key={r.rol_tienda_id}>
                  <td>{r.rol_tienda_id}</td>
                  <td>{r.nombre}</td>
                  <td>
                    <span className={Number(r.estado) === 1 ? "status active" : "status inactive"}>
                      {Number(r.estado) === 1 ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div className="actions">
                      {hasPerm("update_rol") && (
                        <span
                          className="action-icon edit"
                          onClick={() => !updating && openEditModal(r)}
                          style={{ opacity: updating ? 0.6 : 1, pointerEvents: updating ? "none" : "auto" }}
                          title="Editar"
                        >
                          {IconEdit}
                        </span>
                      )}

                      {hasPerm("assign_permissions_rol") && (
                        <span
                          className="action-icon config"
                          onClick={() => !updating && openPermisosModal(r)}
                          style={{ opacity: updating ? 0.6 : 1, pointerEvents: updating ? "none" : "auto" }}
                          title="Asignar permisos"
                        >
                          {IconConfig}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {filtered.length > 0 && (
        <div className="pagination">
          <div className="pagination__center">
            <div className="pagination__nav" aria-label="Paginación de roles">
              <button
                type="button"
                className="pagination__arrow"
                onClick={prevPage}
                disabled={page === 1 || loading}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pagination__pages">
                {visiblePages.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination__page${n === page ? " is-active" : ""}`}
                    onClick={() => goToPage(n)}
                    aria-label={`Ir a página ${n}`}
                    aria-current={n === page ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="pagination__arrow"
                onClick={nextPage}
                disabled={page === totalPages || loading}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <RolFormModal
          isOpen={showModal}
          onClose={() => !updating && setShowModal(false)}
          rolData={selectedRole}
          onSubmit={(formData) => {
            if (selectedRole) return handleUpdate(selectedRole.rol_tienda_id, formData);
            return handleCreate(formData);
          }}
        />
      )}

      {showPermisosModal && roleForPermisos && (
        <AsignarPermisosModal
          rol={roleForPermisos}
          onClose={() => setShowPermisosModal(false)}
        />
      )}
    </div>
  );
}

export default RolesPage;
